/**
 * Central Configuration Module
 * ============================
 *
 * This file consolidates all environment-based configuration:
 * - GitHub Repository URLs
 * - Supabase Dashboard URLs
 *
 * Environment Variables:
 * - VITE_GITHUB_REPOSITORY: Format "owner/repo"
 * - VITE_SUPABASE_PROJECT_ID: Supabase project reference
 */

// =============================================================================
// GITHUB REPOSITORY CONFIGURATION
// =============================================================================

const GITHUB_REPOSITORY = import.meta.env.VITE_GITHUB_REPOSITORY || "Dotmaster-ORG/checkmaty-app";

// Parse owner and repo from the combined string
const [GITHUB_OWNER, GITHUB_REPO] = GITHUB_REPOSITORY.split("/");

const GITHUB_BRANCH = import.meta.env.VITE_GITHUB_BRANCH || "main";
const GITHUB_BASE_URL = `https://github.com/${GITHUB_OWNER}/${GITHUB_REPO}`;

/**
 * Helper functions to generate GitHub URLs
 */
export const getGitHubUrl = {
  /** Repository root URL */
  root: () => GITHUB_BASE_URL,

  /** File/folder in repository (blob view) */
  blob: (path: string) => `${GITHUB_BASE_URL}/blob/${GITHUB_BRANCH}/${path}`,

  /** GitHub Actions overview */
  actions: () => `${GITHUB_BASE_URL}/actions`,

  /** Specific workflow */
  workflow: (workflowFile: string) => `${GITHUB_BASE_URL}/actions/workflows/${workflowFile}`,

  /** Workflow status badge */
  badge: (workflowFile: string) => `${GITHUB_BASE_URL}/actions/workflows/${workflowFile}/badge.svg`,

  /** Repository settings (secrets, variables, etc.) */
  settings: (subPath?: string) =>
    subPath ? `${GITHUB_BASE_URL}/settings/${subPath}` : `${GITHUB_BASE_URL}/settings`,
};

// =============================================================================
// SUPABASE DASHBOARD CONFIGURATION
// =============================================================================

const SUPABASE_PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID ?? "";

if (!SUPABASE_PROJECT_ID) {
  // TES-700: Die Project-ID wird nur fuer Dashboard-Links gebraucht (SetupStatus,
  // GitHubConfigDocumentation, SetupValidationWarning). Ein fehlender Build-Wert
  // darf den App-Start nicht crashen (Module-Init-Throw => ewiger statischer
  // Splash auf Deploy-Previews) — warnen statt werfen. Die echten Runtime-
  // Pflicht-Vars (VITE_SUPABASE_URL/PUBLISHABLE_KEY) werfen weiterhin in client.ts.
  console.warn(
    "[config] VITE_SUPABASE_PROJECT_ID ist nicht gesetzt — Supabase-Dashboard-Links sind unvollständig.",
  );
}

const SUPABASE_DASHBOARD_BASE = `https://supabase.com/dashboard/project/${SUPABASE_PROJECT_ID}`;

/**
 * Helper functions to generate Supabase Dashboard URLs
 */
export const getSupabaseUrl = {
  /** SQL Editor */
  sqlEditor: () => `${SUPABASE_DASHBOARD_BASE}/sql/new`,

  /** Authentication */
  auth: () => `${SUPABASE_DASHBOARD_BASE}/auth/users`,
  authProviders: () => `${SUPABASE_DASHBOARD_BASE}/auth/providers`,
  urlConfig: () => `${SUPABASE_DASHBOARD_BASE}/auth/url-configuration`,

  /** Edge Functions */
  functions: () => `${SUPABASE_DASHBOARD_BASE}/functions`,
  functionLogs: (name: string) => `${SUPABASE_DASHBOARD_BASE}/functions/${name}/logs`,
  functionsSecrets: () => `${SUPABASE_DASHBOARD_BASE}/settings/functions`,

  /** Storage */
  storage: () => `${SUPABASE_DASHBOARD_BASE}/storage/buckets`,

  /** Settings */
  settings: () => `${SUPABASE_DASHBOARD_BASE}/settings`,
};
